import React, { useState } from "react";
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  MenuItem,
  Alert,
  Divider,
  Container,
  Grid,
  InputAdornment,
  CircularProgress,
  Snackbar,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { motion } from "framer-motion";
import { useForm, Controller } from "react-hook-form";
import axios from "axios";
import {
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Business as BusinessIcon,
  AccountCircle as AccountIcon,
  CheckCircle as CheckIcon,
} from "@mui/icons-material";
import config from "../config.js";

const roleOptions = [
  { value: "user", label: "Client" },
  { value: "company", label: "Company" },
  { value: "LLP", label: "LLP" },
  { value: "ca", label: "Chartered Accountant" },
  { value: "cs", label: "Company Secretary" },
  { value: "lawyer", label: "Lawyer" },
  { value: "employee", label: "Employee" },
];

const SuccessScreen = ({ successData, onCreateAnother }) => {
  const theme = useTheme();

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5 }}
      >
        <Card
          elevation={8}
          sx={{
            background: `linear-gradient(135deg, ${
              theme.palette.success.main
            } 0%, ${
              theme.palette.success.light
            } 100%)`,
            color: theme.palette.common.white,
            textAlign: "center",
            py: 4,
          }}
        >
          <CardContent>
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
            >
              <CheckIcon sx={{ fontSize: 80, mb: 2 }} />
            </motion.div>

            <Typography
              variant="h4"
              gutterBottom
              sx={{
                background: `linear-gradient(45deg, ${theme.palette.common.white} 30%, rgba(255,255,255,0.8) 90%)`,
                backgroundClip: "text",
                textFillColor: "transparent",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              Sub-User Created Successfully!
            </Typography>

            <Typography variant="body1" sx={{ mb: 3, opacity: 0.9 }}>
              Login credentials have been sent via email and WhatsApp
            </Typography>

            <Box
              sx={{
                background: "rgba(255,255,255,0.1)",
                backdropFilter: "blur(10px)",
                borderRadius: theme.shape.borderRadius / 8,
                p: 3,
                mb: 3,
              }}
            >
              <Typography variant="h6" gutterBottom>
                Account Details:
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography>
                    <strong>Username:</strong> {successData?.username}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography>
                    <strong>Email:</strong> {successData?.email}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography>
                    <strong>Role:</strong> {successData?.role}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography>
                    <strong>Prefix:</strong> {successData?.subUserPrefix}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography>
                    <strong>Login Code:</strong> {successData?.loginCode}
                  </Typography>
                </Grid>
              </Grid>
            </Box>

            <Button
              variant="contained"
              color="secondary"
              size="large"
              onClick={onCreateAnother}
              sx={{
                mt: 2,
                borderRadius: theme.shape.borderRadius / 4,
                boxShadow: theme.shadows[8],
              }}
            >
              Create Another Sub-User
            </Button>
          </CardContent>
        </Card>
      </motion.div>
    </Container>
  );
};

const SignupForm = ({ onSubmit, loading, error, onCloseError }) => {
  const theme = useTheme();

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: {
      username: "",
      email: "",
      phone: "",
      role: "user",
    },
  });

  const textFieldStyles = {
    "& .MuiOutlinedInput-root": {
      borderRadius: theme.shape.borderRadius ? theme.shape.borderRadius / 4 : 4,
      "&:hover": {
        "& .MuiOutlinedInput-notchedOutline": {
          borderColor: theme.palette.primary.main,
        },
      },
    },
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: `linear-gradient(135deg, ${theme.palette.secondary.light} 0%, ${theme.palette.secondary.main} 100%)`,

        py: 4,
      }}
    >
      <Container maxWidth="md">
        <motion.div
          initial={{ opacity: 0, y: -50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          {/* Logo Section placeholder */}
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <Card
            elevation={24}
            sx={{
              backdropFilter: "blur(20px)",
              background: "rgba(255,255,255,0.95)",
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: theme.shape.borderRadius / 2,
            }}
          >
            <CardContent sx={{ p: 4 }}>
              <Typography
                variant="h4"
                align="center"
                gutterBottom
                sx={{
                  mb: 3,
                  background: `linear-gradient(45deg, ${theme.palette.primary.main} 30%, ${theme.palette.primary.light} 90%)`,
                  backgroundClip: "text",
                  textFillColor: "transparent",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                Create Sub-User Account
              </Typography>

              <Divider
                sx={{
                  mb: 4,
                  background: `linear-gradient(90deg, transparent, ${theme.palette.primary.main}, transparent)`,
                  height: 2,
                }}
              />

              <form onSubmit={handleSubmit(onSubmit)}>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <Controller
                      name="username"
                      control={control}
                      rules={{
                        required: "Username is required",
                        minLength: {
                          value: 2,
                          message: "Username must be at least 2 characters",
                        },
                      }}
                      render={({ field }) => (
                        <motion.div
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: 0.3 }}
                        >
                          <TextField
                            {...field}
                            fullWidth
                            label="Username"
                            error={!!errors.username}
                            helperText={errors.username?.message}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <PersonIcon
                                    sx={{ color: theme.palette.primary.main }}
                                  />
                                </InputAdornment>
                              ),
                            }}
                            sx={textFieldStyles}
                          />
                        </motion.div>
                      )}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Controller
                      name="role"
                      control={control}
                      rules={{ required: "Role is required" }}
                      render={({ field }) => (
                        <motion.div
                          initial={{ opacity: 0, x: 20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: 0.4 }}
                        >
                          <TextField
                            {...field}
                            select
                            fullWidth
                            label="Role"
                            error={!!errors.role}
                            helperText={errors.role?.message}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <BusinessIcon
                                    sx={{ color: theme.palette.primary.main }}
                                  />
                                </InputAdornment>
                              ),
                            }}
                            sx={textFieldStyles}
                          >
                            {roleOptions.map((option) => (
                              <MenuItem key={option.value} value={option.value}>
                                {option.label}
                              </MenuItem>
                            ))}
                          </TextField>
                        </motion.div>
                      )}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Controller
                      name="email"
                      control={control}
                      rules={{
                        required: "Email is required",
                        pattern: {
                          value: /^\S+@\S+$/i,
                          message: "Please enter a valid email address",
                        },
                      }}
                      render={({ field }) => (
                        <motion.div
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: 0.5 }}
                        >
                          <TextField
                            {...field}
                            fullWidth
                            label="Email Address"
                            type="email"
                            error={!!errors.email}
                            helperText={errors.email?.message}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <EmailIcon
                                    sx={{ color: theme.palette.primary.main }}
                                  />
                                </InputAdornment>
                              ),
                            }}
                            sx={textFieldStyles}
                          />
                        </motion.div>
                      )}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Controller
                      name="phone"
                      control={control}
                      rules={{
                        required: "Phone number is required",
                        minLength: {
                          value: 10,
                          message: "Phone number must be at least 10 digits",
                        },
                      }}
                      render={({ field }) => (
                        <motion.div
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: 0.6 }}
                        >
                          <TextField
                            {...field}
                            fullWidth
                            label="Phone Number"
                            type="tel"
                            error={!!errors.phone}
                            helperText={errors.phone?.message}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <PhoneIcon
                                    sx={{ color: theme.palette.primary.main }}
                                  />
                                </InputAdornment>
                              ),
                            }}
                            sx={textFieldStyles}
                          />
                        </motion.div>
                      )}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.7 }}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <Button
                        type="submit"
                        fullWidth
                        variant="contained"
                        size="large"
                        disabled={loading}
                        sx={{
                          py: 1.5,
                          borderRadius: theme.shape.borderRadius
                            ? theme.shape.borderRadius / 4
                            : 4,
                          fontSize: "1.1rem",
                          fontWeight: "bold",
                          background: `linear-gradient(45deg, ${theme.palette.primary.main} 30%, ${theme.palette.primary.light} 90%)`,
                          boxShadow: theme.shadows[6],
                          "&:hover": {
                            background: `linear-gradient(45deg, ${theme.palette.primary.dark} 30%, ${theme.palette.primary.main} 90%)`,
                            boxShadow: theme.shadows[8],
                            transform: "translateY(-2px)",
                          },
                          "&:disabled": {
                            background: theme.palette.action.disabledBackground,
                            boxShadow: "none",
                          },
                        }}
                      >
                        {loading ? (
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 2,
                            }}
                          >
                            <CircularProgress size={24} color="inherit" />
                            Creating Account...
                          </Box>
                        ) : (
                          <>
                            <AccountIcon sx={{ mr: 1 }} />
                            Create Sub-User Account
                          </>
                        )}
                      </Button>
                    </motion.div>
                  </Grid>
                </Grid>
              </form>

              <Box
                sx={{
                  mt: 3,
                  p: 2,
                  bgcolor: `${theme.palette.primary.main}1A`,
                  borderRadius: theme.shape.borderRadius / 8,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{ color: theme.palette.text.secondary }}
                  align="center"
                >
                  📧 Login credentials will be automatically sent via email and
                  WhatsApp
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </motion.div>

        {/* Error Snackbar */}
        <Snackbar
          open={!!error}
          autoHideDuration={6000}
          onClose={onCloseError}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
        >
          <Alert
            onClose={onCloseError}
            severity="error"
            variant="filled"
            sx={{
              width: "100%",
              borderRadius: theme.shape.borderRadius / 8,
            }}
          >
            {error}
          </Alert>
        </Snackbar>
      </Container>
    </Box>
  );
};

const SubUserSignup = () => {
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");
  const [successData, setSuccessData] = useState(null);
  const { API_BASE_URL } = config;

  const onSubmit = async (data) => {
    setLoading(true);
    setError("");

    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/v1/subuser-signup`,
        data,
        {
          headers: {
            "Content-Type": "application/json",
          },
          withCredentials: true,
        }
      );

      setSuccessData(response.data.data);
      setSuccess(true);
    } catch (err) {
      setError(
        err.response?.data?.message ||
          "An error occurred while creating the sub-user account"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleCloseSnackbar = () => {
    setError("");
  };

  const handleCreateAnother = () => {
    setSuccess(false);
    setSuccessData(null);
  };

  return (
    <>
      {success ? (
        <SuccessScreen
          successData={successData}
          onCreateAnother={handleCreateAnother}
        />
      ) : (
        <SignupForm
          onSubmit={onSubmit}
          loading={loading}
          error={error}
          onCloseError={handleCloseSnackbar}
        />
      )}
    </>
  );
};

export default SubUserSignup;
